import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { ICONS } from "../../../constants/icons"
import {
  EMPTY_PERMISSION_SELECTION,
  PERMISSION_COLUMNS,
  PERMISSION_MODULES,
  isPermissionCellDisabled,
  isPermissionUnavailable,
  normalizePermissionSelection,
} from "./adminRolePermissionsData"

const PermissionCheckboxes = ({
  targetId,
  value,
  onChange,
  readOnly,
  availableLevels,
}) => {
  const selection = normalizePermissionSelection(value, availableLevels)

  return (
    <>
      {PERMISSION_COLUMNS.map(column => {
        const unavailable = isPermissionUnavailable({
          columnKey: column.key,
          availableLevels,
        })

        if (unavailable) {
          return (
            <span
              key={`${targetId}-${column.key}`}
              className="admin-roles-create__permission-cell admin-roles-create__permission-cell--unavailable"
              aria-label={`${column.label} not available`}
            >
              <span
                className="admin-roles-create__permission-dash"
                aria-hidden="true"
              >
                –
              </span>
            </span>
          )
        }

        const disabled = isPermissionCellDisabled({
          columnKey: column.key,
          selection,
          availableLevels,
          readOnly,
        })
        const checked = Boolean(selection[column.key])

        return (
          <label
            key={`${targetId}-${column.key}`}
            className={`admin-roles-create__permission-cell${
              readOnly ? " admin-roles-create__permission-cell--readonly" : ""
            }${disabled ? " admin-roles-create__permission-cell--disabled" : ""}`}
          >
            <input
              type="checkbox"
              name={`permission-${targetId}-${column.key}`}
              value={column.key}
              checked={checked}
              onChange={() => onChange(targetId, column.key)}
              disabled={disabled}
              className="admin-roles-create__permission-input"
            />
            <span
              className="admin-roles-create__permission-mark"
              aria-hidden="true"
            />
            <span className="visually-hidden">{column.label}</span>
          </label>
        )
      })}
    </>
  )
}

PermissionCheckboxes.propTypes = {
  targetId: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  availableLevels: PropTypes.arrayOf(PropTypes.string),
}

PermissionCheckboxes.defaultProps = {
  value: EMPTY_PERMISSION_SELECTION,
  readOnly: false,
  availableLevels: null,
}

const ModuleScreenLabel = ({
  icon,
  iconTone = "neutral",
  label,
  expandable = false,
  expanded = false,
  onToggle,
}) => {
  const iconSrc = icon || ICONS.adminGrid4
  const toneKey = iconTone || "neutral"

  const content = (
    <>
      <span
        className={`admin-roles-create__module-icon-wrap admin-roles-create__module-icon-wrap--${toneKey}`}
      >
        <span
          className="admin-roles-create__module-icon"
          style={{
            WebkitMaskImage: `url(${iconSrc})`,
            maskImage: `url(${iconSrc})`,
          }}
          aria-hidden="true"
        />
      </span>
      <span className="admin-roles-create__module-text">
        {expandable ? (
          <img
            src={ICONS.chevronDown}
            alt=""
            width={12}
            height={12}
            className={`admin-roles-create__module-chevron${
              expanded ? " admin-roles-create__module-chevron--expanded" : ""
            }`}
          />
        ) : null}
        <span className="admin-roles-create__module-name">{label}</span>
      </span>
    </>
  )

  if (expandable) {
    return (
      <button
        type="button"
        className="admin-roles-create__module-label admin-roles-create__module-label--toggle"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        {content}
      </button>
    )
  }

  return (
    <div className="admin-roles-create__module-label" role="rowheader">
      {content}
    </div>
  )
}

ModuleScreenLabel.propTypes = {
  icon: PropTypes.string,
  iconTone: PropTypes.string,
  label: PropTypes.string.isRequired,
  expandable: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
}

ModuleScreenLabel.defaultProps = {
  icon: null,
  iconTone: "neutral",
  expandable: false,
  expanded: false,
  onToggle: undefined,
}

/**
 * Permission grid for Create / Edit / Detail Role.
 * Pass API-mapped `modules` when available; falls back to static Figma modules.
 */
const RolePermissionsMatrix = ({
  permissions,
  onChange,
  modules: modulesProp,
  readOnly = false,
  showCollapsedModuleRadios = false,
  defaultExpandedOverride,
  error = "",
}) => {
  const modules =
    Array.isArray(modulesProp) && modulesProp.length > 0
      ? modulesProp
      : PERMISSION_MODULES

  const initialExpanded = useMemo(() => {
    const fallbackExpanded =
      defaultExpandedOverride !== undefined
        ? defaultExpandedOverride
        : undefined

    return modules.reduce((acc, module) => {
      if (module.screens?.length) {
        acc[module.id] =
          fallbackExpanded !== undefined
            ? fallbackExpanded
            : module.defaultExpanded ?? false
      }
      return acc
    }, {})
  }, [defaultExpandedOverride, modules])

  const [expandedModules, setExpandedModules] = useState(initialExpanded)

  const toggleModule = moduleId => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }))
  }

  return (
    <div className="admin-roles-create__permissions">
      {error ? (
        <p className="amok-field__message amok-field__message--error">
          {error}
        </p>
      ) : null}
      <div
        className="admin-roles-create__permissions-table"
        role="table"
        aria-label="Role permissions"
      >
        <div
          className="admin-roles-create__permissions-row admin-roles-create__permissions-row--head"
          role="row"
        >
          <div role="columnheader">Module/Screen</div>
          {PERMISSION_COLUMNS.map(column => (
            <div key={column.key} role="columnheader">
              {column.label}
            </div>
          ))}
        </div>

        {modules.map(module => {
          const hasScreens = Boolean(module.screens?.length)
          const isExpanded = expandedModules[module.id]
          const availableLevels = module.availableLevels || null

          if (hasScreens && isExpanded) {
            return (
              <div
                key={module.id}
                className="admin-roles-create__permissions-group"
              >
                <ModuleScreenLabel
                  icon={module.icon}
                  iconTone={module.iconTone}
                  label={module.label}
                  expandable
                  expanded={isExpanded}
                  onToggle={() => toggleModule(module.id)}
                />

                {module.screens.map(screen => (
                  <div
                    key={screen.id}
                    className="admin-roles-create__permissions-row admin-roles-create__permissions-row--screen"
                    role="row"
                  >
                    <div
                      className="admin-roles-create__screen-label"
                      role="rowheader"
                    >
                      {screen.label}
                    </div>
                    <PermissionCheckboxes
                      targetId={screen.id}
                      value={permissions[screen.id]}
                      onChange={onChange}
                      readOnly={readOnly}
                    />
                  </div>
                ))}
              </div>
            )
          }

          const targetId = module.id
          return (
            <div
              key={module.id}
              className="admin-roles-create__permissions-row"
              role="row"
            >
              {hasScreens ? (
                <ModuleScreenLabel
                  icon={module.icon}
                  iconTone={module.iconTone}
                  label={module.label}
                  expandable
                  expanded={isExpanded}
                  onToggle={() => toggleModule(module.id)}
                />
              ) : (
                <ModuleScreenLabel
                  icon={module.icon}
                  iconTone={module.iconTone}
                  label={module.label}
                />
              )}
              {!hasScreens && (
                <PermissionCheckboxes
                  targetId={targetId}
                  value={permissions[targetId]}
                  onChange={onChange}
                  readOnly={readOnly}
                  availableLevels={availableLevels}
                />
              )}
              {hasScreens && !isExpanded && showCollapsedModuleRadios && (
                <PermissionCheckboxes
                  targetId={targetId}
                  value={permissions[targetId]}
                  onChange={onChange}
                  readOnly={readOnly}
                  availableLevels={availableLevels}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

RolePermissionsMatrix.propTypes = {
  permissions: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  modules: PropTypes.arrayOf(PropTypes.object),
  readOnly: PropTypes.bool,
  showCollapsedModuleRadios: PropTypes.bool,
  defaultExpandedOverride: PropTypes.bool,
  error: PropTypes.string,
}

RolePermissionsMatrix.defaultProps = {
  onChange: () => {},
  modules: null,
  readOnly: false,
  showCollapsedModuleRadios: false,
  defaultExpandedOverride: undefined,
  error: "",
}

export default RolePermissionsMatrix
